import { runtimeConfig } from '@/shared/config'
import { lastSessionKind, rememberSessionKind, sessionExpired } from './client'

// Своей формы входа у продукта НЕТ. Вход обеих аудиторий — единый экран хаба
// (22-unified-login): сотрудник уходит оттуда в Authentik (cookie stit_session),
// заказчик вводит логин и пароль (cookie stit_customer). По 401 продукт не знает,
// кто перед ним, поэтому ведёт именно туда, а не на id.stit.tech.
// Выход разведён по контурам: сотрудника гасит employee-backend, заказчика —
// company-backend (00-architecture § 3).

// Пропускаем только внутренний absolute-path ("/tasks/1"): не "//evil"
// (protocol-relative), не "\..\", без CRLF — защита от open-redirect. Зеркалит
// sanitizeReturnTo BFF — двойная страховка, бэк санитайзит повторно.
export function sanitizeInternalPath(raw: string | null | undefined): string | undefined {
  if (raw?.[0] !== '/' || raw.startsWith('//') || /[\\\r\n]/.test(raw)) return undefined
  return raw
}

// rd для чужого origin — всегда absolute URL: страница входа должна знать, куда
// возвращать. Уже-абсолютный берём как есть (без URL-нормализации, чтобы не
// появлялся хвостовой '/'), относительный путь резолвим через origin.
function absoluteReturnTo(returnTo: string): string {
  if (/^https?:\/\//.test(returnTo)) return returnTo
  try {
    return new URL(returnTo, window.location.origin).href
  } catch {
    return window.location.origin
  }
}

// notice=expired отличает «сессию не удалось продлить» от «ты сюда впервые» —
// экран входа объясняет, почему человека выкинуло.
export function loginUrl(returnTo: string = window.location.href): string {
  const params = new URLSearchParams({ rd: absoluteReturnTo(returnTo) })
  if (sessionExpired()) params.set('notice', 'expired')
  return `${runtimeConfig.HUB_LOGIN_URL}?${params.toString()}`
}

// Реальная браузерная навигация (не router-redirect): уходим на чужой origin
// hub.stit.tech. По умолчанию возвращаемся на текущий полный URL.
export function redirectToLogin(returnTo: string = window.location.href): void {
  window.location.assign(loginUrl(returnTo))
}

// Своей формы входа нет, поэтому «нет сессии» = уводим на вход сразу, без
// промежуточного экрана с кнопкой. Ровно один автопереход за вкладку: если
// вернулись со входа и сессии всё равно нет (cookie не встала, доступ отозван),
// второй прыжок закольцевал бы продукт со страницей входа — вместо этого false, и
// вызывающий показывает экран входа. Флаг живёт в sessionStorage: переживает
// навигацию на чужой origin и умирает вместе со вкладкой.
const LOGIN_ATTEMPT_KEY = 'auth:login-attempt'

export function autoRedirectToLogin(returnTo?: string): boolean {
  // Приватные режимы/выключенный storage кидают на доступе — без памяти о
  // попытке автопереход неотличим от цикла, поэтому отдаём решение экрану.
  try {
    if (sessionStorage.getItem(LOGIN_ATTEMPT_KEY)) return false
    sessionStorage.setItem(LOGIN_ATTEMPT_KEY, '1')
  } catch {
    return false
  }
  redirectToLogin(returnTo)
  return true
}

// Уже ходили на вход в этой вкладке — значит вернулись без сессии.
export function hasLoginAttempt(): boolean {
  try {
    return sessionStorage.getItem(LOGIN_ATTEMPT_KEY) !== null
  } catch {
    return true
  }
}

export function clearLoginAttempt(): void {
  try {
    sessionStorage.removeItem(LOGIN_ATTEMPT_KEY)
  } catch {
    // storage недоступен — нечего чистить
  }
}

// Выход гасит сессию у её issuer'а — контуры независимы, общего логаута нет.
// Сотрудник: id.stit.tech/auth/logout гасит hub-сессию, ставит tombstone и чистит
// cookie (Domain=.stit.tech). Контракт хаба — POST (state-changing; GET → 405 и
// уязвим к prefetch/CSRF-логауту), поэтому не location.assign, а form-POST
// навигация: same-site POST шлёт cookie без CORS, браузер сам следует 302 на ?rd=.
// Заказчик: у company-backend только XHR-ручка (/auth/customer/logout,
// cross-origin с credentials), редиректа по ?rd= у неё нет — гасим сессию запросом
// и уводим на единый вход сами. Сеть отвалилась — уводим всё равно: cookie
// переживёт, но человек не должен залипнуть в приложении.
export function redirectToLogout(returnTo: string = window.location.origin): void {
  if (lastSessionKind() === 'customer') {
    void fetch(`${runtimeConfig.HUB_API_BASE_URL}/auth/customer/logout`, {
      method: 'POST',
      credentials: 'include',
    })
      .catch(() => undefined)
      .finally(() => {
        rememberSessionKind(null)
        window.location.assign(loginUrl(returnTo))
      })
    return
  }
  const form = document.createElement('form')
  form.method = 'post'
  form.action = `${runtimeConfig.ID_BASE_URL}/auth/logout?rd=${encodeURIComponent(absoluteReturnTo(returnTo))}`
  document.body.appendChild(form)
  form.submit()
}
